<template>
  <section class="flex flex-col gap-3 rounded-lg border bg-muted p-5" data-test-id="setup-website-autofill">
    <Label for="setup-website">{{ t('admin.setup.branding.website.label') }}</Label>
    <form class="flex flex-wrap gap-2" @submit.prevent="autofill">
      <Input
        id="setup-website"
        v-model="websiteRef"
        type="url"
        inputmode="url"
        placeholder="https://"
        class="min-w-0 flex-[1_1_15rem] bg-background"
        aria-describedby="setup-website-hint"
        data-test-id="setup-website-input"
      />
      <Button
        type="submit"
        variant="secondary"
        :disabled="!websiteRef.trim() || isAutofillingRef"
        data-test-id="setup-website-autofill"
      >
        <Spinner v-if="isAutofillingRef" aria-hidden="true" />
        {{ t('admin.setup.branding.website.action') }}
      </Button>
    </form>
    <p id="setup-website-hint" class="text-xs text-muted-foreground">{{ t('admin.setup.branding.website.hint') }}</p>
    <p
      v-if="messageRef"
      role="status"
      class="flex items-start gap-2 text-sm leading-normal"
      :class="isFoundRef ? 'text-foreground' : 'text-muted-foreground'"
    >
      <CircleCheckIcon v-if="isFoundRef" class="mt-0.5 size-4 shrink-0 text-success" aria-hidden="true" />
      {{ messageRef }}
    </p>
  </section>
</template>

<script setup lang="ts">
import { CircleCheckIcon } from 'lucide-vue-next'
import { ref } from 'vue'

import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Spinner } from '@/components/ui/spinner'
import { usePlatformSetup } from '@/features/admin/platform-setup/hooks/use-platform-setup.hook'
import { useI18n } from '@/lib/i18n'

const { t } = useI18n()
const { autofillFromWebsite } = usePlatformSetup()

const websiteRef = ref('')
const isAutofillingRef = ref(false)
const messageRef = ref('')
const isFoundRef = ref(false)

async function autofill() {
  const url = websiteRef.value.trim()
  if (!url) return
  isAutofillingRef.value = true
  messageRef.value = ''
  try {
    const { logo, colors } = await autofillFromWebsite(url)
    isFoundRef.value = logo || colors
    messageRef.value =
      logo && colors
        ? t('admin.setup.branding.website.foundAll')
        : colors
          ? t('admin.setup.branding.website.foundColors')
          : logo
            ? t('admin.setup.branding.website.foundLogo')
            : t('admin.setup.branding.website.nothing')
  } catch {
    isFoundRef.value = false
    messageRef.value = t('admin.setup.branding.website.error')
  } finally {
    isAutofillingRef.value = false
  }
}
</script>
